import { TFunction } from "i18next";
import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import styled from "styled-components";

import { AtiraTabs } from "../../components/AtiraTabs";
import { Flex } from "../../components/Flex";
import { AtiraSpacePlanType } from "../../model/plan/types/AtiraSpacePlanType.enum";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { thresholdActions } from "../../redux/threshold/threshold.slice";
import { userSliceSelectors } from "../../redux/user/user.selector";
import { userActions } from "../../redux/user/user.slice";
import { Spacing } from "../../theme/Spacing";
import { AtiraToast } from "../../utils/AtiraToast";
import { SubscriptionCurrentSubscriptionTab } from "./tabs/SubscriptionCurrentSubscriptionTab";
import { SubscriptionHistoryTab } from "./tabs/SubscriptionHistoryTab";
import { SubscriptionPlansTab } from "./tabs/SubscriptionPlansTab";
import { AtiraEmpty } from "../../components/AtiraEmpty";
import { LemonSubscriptionStatus } from "../../model/plan/types/LemonSubscriptionStatus.enum";
import { Banner } from "../../components/Banner";
import { Text } from "../../components/Text";
import { Info } from "../../components/Info";
import { Button } from "../../components/Button";
import { faExternalLink } from "@fortawesome/free-solid-svg-icons/faExternalLink";

const getTabs = (t: TFunction) => [
  {
    key: "plans",
    label: t("common.plans"),
    children: <SubscriptionPlansTab />,
  },
  {
    key: "current-plan",
    label: t("subscription.plan.current"),
    children: <SubscriptionCurrentSubscriptionTab />,
  },
  {
    key: "payment-history",
    label: t("subscription.plan.history"),
    children: <SubscriptionHistoryTab />,
  },
];

const Container = styled(Flex)`
  flex-direction: column;
  gap: ${Spacing.m};
  padding: ${Spacing.m};
  flex-grow: 1;

  .ant-tabs {
    padding: 0;
  }
  .ant-tabs-nav-list {
    gap: ${Spacing.s};
  }
  .ant-tabs-tab {
    padding-inline: ${Spacing.l};
  }
  .ant-tabs-nav {
    margin-bottom: ${Spacing.xl};
  }
`;

export const SubscriptionRoute: React.FC = () => {
  const loggedInUser = useAppSelector(userSliceSelectors.selectLoggedInUser);

  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const timer = useRef<NodeJS.Timer | null>(null);

  const isMember = useAppSelector(userSliceSelectors.selectIsUserOrgMember);
  const user = useAppSelector(userSliceSelectors.selectLoggedInUser);

  const displayBanner = [
    LemonSubscriptionStatus.PAST_DUE,
    LemonSubscriptionStatus.UNPAID,
  ].includes(user?.plan?.lemonPlanData?.status as LemonSubscriptionStatus);

  useEffect(() => {
    dispatch(thresholdActions.getThresholds({ userId: loggedInUser?._id! }));
  }, [dispatch, loggedInUser?._id]);

  useEffect(() => {
    const canWork =
      searchParams.get("success") === "true" &&
      searchParams.get("userId") === loggedInUser?._id &&
      !timer.current;

    if (canWork) {
      AtiraToast.success(t("subscription.polling.began"), { duration: 15 });

      let timeoutId: NodeJS.Timeout;

      const poll = async () => {
        try {
          const user = await dispatch(
            userActions.getLoggedInUser({ userId: loggedInUser?._id! }),
          ).unwrap();

          if (user.plan?.type !== AtiraSpacePlanType.PLAN_0) {
            clearInterval(timer.current as NodeJS.Timeout);
            clearTimeout(timeoutId);
          }
        } catch (e: any) {
          console.error("Failed to fetch the logged-in user:", e);
          clearInterval(timer.current as NodeJS.Timeout);
          clearTimeout(timeoutId);
        }
      };

      timer.current = setInterval(poll, 2000);

      timeoutId = setTimeout(() => {
        clearInterval(timer.current as NodeJS.Timeout);
      }, 120000); // 2 minutes

      const params = new URLSearchParams(window.location.search);
      params.delete("success");
      params.delete("userId");
      window.history.replaceState({}, "", window.location.pathname);

      return () => {
        clearInterval(timer.current as NodeJS.Timeout);
        clearTimeout(timeoutId);
        timer.current = null;
      };
    }
  }, [dispatch, loggedInUser?._id, searchParams, t]);

  return (
    <Flex flexDirection="column">
      <Banner open={displayBanner}>
        <Flex flexDirection="column" gap="m" alignItems="center" padding="s">
          <Text color="sub">
            {t("subscription.page.paymentInfo.banner.title")}
          </Text>
          <Flex justifyContent="center" gap="s" alignItems="center">
            <Info
              info={t("subscription.page.paymentInfo.banner.description")}
              color="white"
            />

            <Button
              icon={faExternalLink}
              hover
              backgroundColor="transparent"
              onClick={() =>
                window.open(
                  user?.plan?.lemonSubscriptionData?.subscription?.urls
                    ?.customer_portal_update_subscription,
                  "_blank",
                )
              }
            />
          </Flex>
        </Flex>
      </Banner>

      <Container>
        {!isMember ? (
          <AtiraTabs items={getTabs(t)} />
        ) : (
          <AtiraEmpty description={t("subscription.memberNote")} />
        )}
      </Container>
    </Flex>
  );
};
